<script lang="ts" setup>
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import type { MapMarker } from '~/components/nrjx/map/index.vue';

// Stores
const { currentSite } = storeToRefs(adminStore());

// Services
const { getNearestWeatherStation } = useWeatherService();

// Composables
const { updateSite } = useSiteApi();
const { t } = useI18n();
const { handleSubmit, meta, setFieldValue } = useForm({
  validationSchema: yup.object({
    weather_station_id: yup.number().required(t('global.field_required')).typeError(t('global.field_required')),
  }),
});

const loading = ref(false);
const loadingStations = ref(false);
const stations = ref<CoreWeatherStation[]>([]);

// Methods
const submit = handleSubmit((values) => {
  loading.value = true;
  if (currentSite.value?.id) {
    updateSite(currentSite.value?.id, values);
  }
  loading.value = false;
});

const loadStations = async () => {
  loadingStations.value = true;
  if (currentSite.value?.id && currentSite.value?.address) stations.value = await getNearestWeatherStation(currentSite.value?.id);
  loadingStations.value = false;
};

const selectStation = (marker: MapMarker) => {
  setFieldValue('weather_station_id', marker.id);
};

// Computed
const markers = computed((): MapMarker[] => {
  return stations.value.map((station) => ({
    id: station.id,
    latitude: station.latitude,
    longitude: station.longitude,
    icon: 'ThermometerSun',
    selected: currentSite.value?.weather_station_id === station.id,
  }));
});

// Lifecycle
onMounted(() => {
  loadStations();
});

watch(
  () => currentSite.value?.address,
  () => {
    loadStations();
  },
  { immediate: true, deep: true },
);
</script>
<template>
  <ui-card :title="$t('admin.weather_stations')">
    <ui-info
      v-if="!currentSite?.lat || !currentSite?.long"
      :message="$t('admin.you_need_to_set_the_site_coordinates_to_display_the_weather_station')"
    />
    <nrjx-map
      v-else
      :loading="loadingStations"
      height="50vh"
      :zoom="10"
      :latitude="currentSite?.lat"
      :longitude="currentSite?.long"
      :markers="markers"
      @click-marker="selectStation"
    ></nrjx-map>
    <template #footer>
      <ui-button :loading="loading" :color="meta.dirty ? 'primary' : 'disabled'" @click="submit()">{{ $t('global.save') }}</ui-button>
    </template>
  </ui-card>
</template>
